import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { LoginRequestType } from "@shared/types/LoginRequestType";
import { environment } from "@shared/environments/environment";
import { UserType } from "@shared/types/UserType";
import { firstValueFrom, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { BusinessRoleType } from "@shared/types/BusinessRoleType";
import { CacheQueryResult, createQueryCache } from "@shared/utils/cache.utils";
import { ClearCacheService } from "../clear-cache/clear-cache.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Injectable({
    providedIn: "root",
})
// The AuthService is responsible for managing authentication state and user credentials.
// It handles user login against the API, stores authentication tokens in session storage,
// tracks login status, and caches the business role to optimize repeated lookups.
export class AuthService {
    private businessRoleCache: CacheQueryResult<BusinessRoleType> = createQueryCache(
        () => {
            return firstValueFrom(
                this.http.get<BusinessRoleType>(environment.urlservbeautech + "/api/Values/GetBusinessRole"),
            );
        },
        10 * 60 * 1000,
    );

    private refreshTokenCache: CacheQueryResult<UserType, [string]> = createQueryCache(
        (refreshToken: string) => {
            return firstValueFrom(
                this.http.post<UserType>(environment.urlservbeautech + "/api/Account/refresh", { refreshToken }),
            ).then((user: UserType) => {
                this.user = user;
                sessionStorage.setItem("accessToken", user.token);
                localStorage.setItem("refreshToken", user.refreshToken);
                return user;
            });
        },
        10000, // 10 seconds TTL to handle simultaneous in-flight 401s errors
    );

    private user: UserType | null = null;

    private http: HttpClient = inject(HttpClient);
    private clearCacheService: ClearCacheService = inject(ClearCacheService);

    constructor() {
        this.clearCacheService.logout$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.user = null;
        });
    }

    login(email: string, password: string): Observable<UserType> {
        const loginInfo: LoginRequestType = {
            login: email,
            password: password,
        };

        return this.http.post<UserType>(environment.urlservbeautech + "/api/Account", loginInfo).pipe(
            tap((user: UserType) => {
                this.user = user;
                sessionStorage.setItem("accessToken", user.token);
                localStorage.setItem("refreshToken", user.refreshToken);
                sessionStorage.setItem("email", email);
                // sessionStorage.setItem("password", password);
            }),
        );
    }

    refreshToken(): Promise<UserType> {
        const refreshToken: string | null = localStorage.getItem("refreshToken");

        if (!refreshToken) {
            return Promise.reject(new Error("No refresh token available"));
        }

        return this.refreshTokenCache.fetch(refreshToken);
    }

    isLoggedIn(): boolean {
        return this.user !== null;
    }

    getUser(): UserType | null {
        return this.user;
    }

    getBusinessRole(): Promise<BusinessRoleType> {
        return this.businessRoleCache.fetch();
    }
}
